"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.findMatchingSignatures = exports.isVariadic = exports.isOptionalT = exports.isOneOfT = exports.DateDiffUnits = exports.DurationUnits = exports.parseT = exports.isNumeric = exports.variadic = exports.optional = exports.oneOf = exports.T = void 0;
const lodash_1 = require("lodash");
const representation_1 = require("./representation");
var T;
(function (T) {
    T["string"] = "String";
    T["number"] = "Number";
    T["float"] = "Float";
    T["integer"] = "Integer";
    T["boolean"] = "Boolean";
    T["date"] = "Date";
    T["object"] = "Object";
    T["interval"] = "Interval";
    T["enum"] = "Enum";
    T["reference"] = "Reference";
})(T || (exports.T = T = {}));
const oneOf = (...values) => ({ $oneOf: values });
exports.oneOf = oneOf;
const optional = (value) => ({ $optional: value });
exports.optional = optional;
const variadic = (...values) => ({ $variadic: values });
exports.variadic = variadic;
const isNumeric = (type) => type === T.number || type === T.float || type === T.integer;
exports.isNumeric = isNumeric;
const parseT = (type) => {
    switch (type) {
        case 'String':
            return T.string;
        case 'Number':
            return T.number;
        case 'Float':
            return T.float;
        case 'Integer':
            return T.integer;
        case 'Boolean':
            return T.boolean;
        case 'Date':
            return T.date;
        case 'Interval':
            return T.interval;
        case 'Object':
            return T.object;
        case 'Enum':
            return T.enum;
        case 'Reference':
            return T.reference;
        case null:
            return null;
        default:
            throw new Error(`Unknown type ${type}`);
    }
};
exports.parseT = parseT;
exports.DurationUnits = ['year', 'month', 'week', 'day', 'hour', 'minute', 'second', 'millisecond'];
// Units supported by "date_diff" function.
exports.DateDiffUnits = ['year', 'quarter', 'month', 'week', 'day', 'hour', 'minute', 'second'];
const isOneOfT = (value) => (0, lodash_1.isObject)(value) && '$oneOf' in value && (0, lodash_1.isArray)(value.$oneOf);
exports.isOneOfT = isOneOfT;
const isOptionalT = (value) => (0, lodash_1.isObject)(value) && '$optional' in value && ((0, lodash_1.isString)(value.$optional) || (0, exports.isOneOfT)(value.$optional));
exports.isOptionalT = isOptionalT;
const isVariadic = (value) => (0, lodash_1.isObject)(value) && '$variadic' in value && (0, lodash_1.isArray)(value.$variadic);
exports.isVariadic = isVariadic;
const matchTypes = (received, expected) => {
    if ((0, exports.isOptionalT)(expected) && (0, exports.isOneOfT)(expected.$optional)) {
        return matchTypes(received, expected.$optional);
    }
    if ((0, exports.isOptionalT)(expected)) {
        return received === expected.$optional || received === null;
    }
    if ((0, exports.isOneOfT)(expected)) {
        return expected.$oneOf.some((type) => matchTypes(received, type));
    }
    return received === expected || received === null || expected === null;
};
const variadicArguments = (length, variadicArguments) => {
    const needLength = (0, lodash_1.floor)(length / variadicArguments.length);
    return (0, lodash_1.flatten)((0, lodash_1.times)(needLength, () => variadicArguments));
};
const matchArgumentTypes = (args, expectedTypes) => (0, lodash_1.zip)(args, expectedTypes)
    .map(([argument, expected]) => {
    if ((0, exports.isOptionalT)(expected) && argument?.returnType === undefined) {
        return { argument, matchingType: true, expected };
    }
    if (expected === undefined || argument?.returnType === undefined) {
        return { argument, matchingType: false, expected: expected ?? null };
    }
    if (expected === T.reference && (0, representation_1.isFieldNode)(argument.node)) {
        return { argument, matchingType: true, expected };
    }
    return { argument, matchingType: matchTypes(argument.returnType, expected), expected };
})
    .map(({ argument, matchingType, expected }) => ({
    node: argument?.node,
    matchingType,
    expected,
    received: argument?.returnType ?? null,
}));
const argumentsForSignature = (signature, length) => (0, lodash_1.flatten)(signature.arguments.map((argument) => (0, exports.isVariadic)(argument) ? variadicArguments(length, argument.$variadic) : argument));
const findMatchingSignatures = (args, def) => {
    const matches = def.signatures
        .map((signature) => ({
        arguments: matchArgumentTypes(args, argumentsForSignature(signature, args.length)),
        signature,
    }))
        // Sort closest matching signatures first -- the ones having more matching types starting the beginning of arguments
        // are preferred. For example [true, true, false] is preferred over [true, false, true] even though they have the
        // same number of matching types.
        .sort((first, second) => {
        const a = first.arguments.map(({ matchingType }) => matchingType);
        const b = second.arguments.map(({ matchingType }) => matchingType);
        const shortest = Math.min(a.length, b.length);
        for (let i = 0; i < shortest; i++) {
            if (a[i] && !b[i]) {
                return -1;
            }
            if (!a[i] && b[i]) {
                return 1;
            }
        }
        return 0;
    });
    const signatures = matches.filter((m) => m.arguments.every((r) => r.matchingType)).map((m) => m.signature);
    if (signatures.length === 0) {
        return { type: 'failure', closestMatch: (0, lodash_1.first)(matches) };
    }
    return { type: 'success', signatures };
};
exports.findMatchingSignatures = findMatchingSignatures;
//# sourceMappingURL=definition.js.map